import { useEffect, useState } from 'react';
import { useRxCollection } from 'rxdb-hooks';

import { useMst } from '../../mobx-models/Root';
import {
  InspectionStatus,
  InspectorRole,
  TaskStatus,
  WorkPackageStatus,
} from '../../utilities/enums';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import type {
  InspectionListCollection as InspectionListCollection,
  InspectionListDocument,
} from '../inspectionList/inspectionList.types';
import { RxdbCollectionName } from '../rxdbCollectionName';
import { WorkPackagesCollection } from '../workPackages/workPackages.types';
import type { TaskList, TaskListCollection, TaskListDocument } from './taskList.types';

export const useTaskList = () => {
  const taskListCollection: TaskListCollection = useRxCollection(RxdbCollectionName.TASK_LIST);
  const { CurrentUserSelected, SiteDiscipline } = useMst();

  const inspectionListCollection: InspectionListCollection = useRxCollection(
    RxdbCollectionName.INSPECTION_LIST,
  );

  const workPackagesCollection: WorkPackagesCollection = useRxCollection(
    RxdbCollectionName.WORK_PACKAGES,
  );

  const [taskListInitialized, setTaskListInitialized] = useState<boolean>(false);
  const site = SiteDiscipline.siteId;
  const discipline = SiteDiscipline.disciplineId;

  useEffect(() => {
    if (taskListCollection) setTaskListInitialized(true);
  }, [taskListCollection]);

  const setInspectionStatus = async (inspectionId: string, status: number) => {
    if (inspectionListCollection) {
      await inspectionListCollection.findOne({ selector: { inspectionId } }).update({
        $set: { assetInspectionStatusId: status },
      });

      if (status === InspectionStatus.IN_PROGRESS) {
        const result = await inspectionListCollection
          .findOne({ selector: { inspectionId } })
          .exec();
        if (workPackagesCollection) {
          await workPackagesCollection
            .findOne({ selector: { workPackageId: { $eq: result?.workPackageId.toString() } } })
            .update({ $set: { workPackageStatusId: WorkPackageStatus.IN_PROGRESS } });
        }
      }
    }
  };

  const updateStatusFromLiReview = async (
    allTasksUnderSameInspection: TaskList[],
    fkAssetInspection: number,
  ) => {
    const siblingTasks = allTasksUnderSameInspection.filter(
      task =>
        ![
          TaskStatus.LI_PENDING_APPROVAL,
          TaskStatus.LI_SIGN_PENDING,
          TaskStatus.SC_PENDING_APPROVAL,
          TaskStatus.COMPLETED,
        ].includes(task.fkInspectionTaskStatusCategory),
    );

    if (siblingTasks.length === 0) {
      await setInspectionStatus(String(fkAssetInspection), InspectionStatus.LI_PENDING_APPROVAL);
    } else if (
      siblingTasks.filter(
        task =>
          task.fkInspectionTaskStatusCategory === TaskStatus.ASSIGNED ||
          task.fkInspectionTaskStatusCategory === TaskStatus.IN_PROGRESS,
      ).length > 0
    ) {
      await setInspectionStatus(String(fkAssetInspection), InspectionStatus.IN_PROGRESS);
    } else {
      await setInspectionStatus(String(fkAssetInspection), InspectionStatus.FI_SIGN_PENDING);
    }
  };

  const updateStatusToLiSign = async (allTaskUnderInspection: TaskList[], inspectionId: number) => {
    const siblingTasks = allTaskUnderInspection.filter(
      task =>
        ![
          TaskStatus.LI_SIGN_PENDING,
          TaskStatus.SC_PENDING_APPROVAL,
          TaskStatus.COMPLETED,
        ].includes(task.fkInspectionTaskStatusCategory),
    );

    if (siblingTasks.length === 0) {
      await setInspectionStatus(String(inspectionId), InspectionStatus.LI_SIGN_PENDING);
    } else if (
      siblingTasks.filter(
        task =>
          task.fkInspectionTaskStatusCategory === TaskStatus.ASSIGNED ||
          task.fkInspectionTaskStatusCategory === TaskStatus.IN_PROGRESS,
      ).length > 0
    ) {
      await setInspectionStatus(String(inspectionId), InspectionStatus.IN_PROGRESS);
    } else if (
      siblingTasks.filter(
        task => task.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING,
      ).length > 0
    ) {
      await setInspectionStatus(String(inspectionId), InspectionStatus.FI_SIGN_PENDING);
    } else {
      await setInspectionStatus(String(inspectionId), InspectionStatus.LI_PENDING_APPROVAL);
    }
  };

  const setTaskListInfo = async (
    status: number,
    complete?: boolean,
    dateCompleted?: string,
    proxyInspector?: string,
  ) => {
    try {
      let result: TaskList | null = null;
      if (taskListCollection) {
        const existingTask: TaskList | null = await taskListCollection
          .findOne({
            selector: { pkAssetInspectionTask: CurrentUserSelected.taskId },
          })
          .exec();
        const roleId =
          existingTask?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL
            ? InspectorRole.LEAD
            : CurrentUserSelected?.currentUser?.clerkId
            ? InspectorRole.DEC
            : InspectorRole.FIELD;
        if (existingTask && existingTask?.fkInspectionTaskStatusCategory !== status) {
          if (complete) {
            result = await taskListCollection
              .findOne({
                selector: {
                  pkAssetInspectionTask: CurrentUserSelected.taskId,
                },
              })
              .update({
                $set: {
                  fkInspectionTaskStatusCategory: status,
                  dateCompleted,
                  isAssignedBack: false,
                  role: roleId,
                  proxyInspector: proxyInspector || '',
                },
              });
          } else {
            result = await taskListCollection
              .findOne({
                selector: {
                  pkAssetInspectionTask: CurrentUserSelected.taskId,
                },
              })
              .update({
                $set: {
                  fkInspectionTaskStatusCategory: status,
                  role: roleId,
                  proxyInspector: proxyInspector || '',
                },
              });
          }
          const fkAssetInspection = existingTask?.fkAssetInspection;
          if (status === TaskStatus.IN_PROGRESS && result) {
            await setInspectionStatus(String(fkAssetInspection), InspectionStatus.IN_PROGRESS);
          } else {
            const allTasksUnderSameInspection = await taskListCollection
              .find({ selector: { $and: [{ fkAssetInspection }, { isActive: { $eq: true } }] } })
              .exec();
            if (status === TaskStatus.LI_PENDING_APPROVAL && result) {
              await updateStatusFromLiReview(allTasksUnderSameInspection, fkAssetInspection);
            } else if (status === TaskStatus.SC_PENDING_APPROVAL && result) {
              const taskResult = await taskListCollection
                .find({
                  selector: {
                    $and: [
                      { fkAssetInspection: CurrentUserSelected.inspectionId },
                      { fkInspectionTaskStatusCategory: { $lt: TaskStatus.FI_SIGN_PENDING } },
                      { isActive: { $eq: true } },
                    ],
                  },
                })
                .exec();
              if (
                taskResult.filter(
                  (i: TaskList) =>
                    i.fkInspectionTaskStatusCategory === TaskStatus.IN_PROGRESS ||
                    i.fkInspectionTaskStatusCategory === TaskStatus.ASSIGNED,
                )?.length === 0
              ) {
                if (
                  taskResult.filter(
                    (i: TaskList) =>
                      i.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL,
                  )?.length === 0
                ) {
                  await setInspectionStatus(
                    String(fkAssetInspection),
                    InspectionStatus.SC_PENDING_APPROVAL,
                  );
                } else {
                  await setInspectionStatus(
                    String(fkAssetInspection),
                    InspectionStatus.LI_PENDING_APPROVAL,
                  );
                }
              }
            } else if (status === TaskStatus.FI_SIGN_PENDING && result) {
              const taskResult = await taskListCollection
                .find({
                  selector: {
                    $and: [
                      { fkAssetInspection: CurrentUserSelected.inspectionId },
                      { fkInspectionTaskStatusCategory: { $lt: TaskStatus.LI_SIGN_PENDING } },
                      { isActive: { $eq: true } },
                    ],
                  },
                })
                .exec();
              if (
                taskResult.filter(
                  (i: TaskList) =>
                    i.fkInspectionTaskStatusCategory === TaskStatus.IN_PROGRESS ||
                    i.fkInspectionTaskStatusCategory === TaskStatus.ASSIGNED,
                )?.length === 0
              ) {
                if (
                  taskResult.filter(
                    (i: TaskList) =>
                      i.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING,
                  )?.length === 0
                ) {
                  if (
                    taskResult.filter(
                      (i: TaskList) =>
                        i.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL,
                    )?.length === 0
                  ) {
                    if (
                      taskResult.filter(
                        (i: TaskList) =>
                          i.fkInspectionTaskStatusCategory === TaskStatus.SC_PENDING_APPROVAL,
                      )?.length === 0
                    ) {
                      await setInspectionStatus(
                        String(fkAssetInspection),
                        InspectionStatus.FI_SIGN_PENDING,
                      );
                    } else {
                      await setInspectionStatus(
                        String(fkAssetInspection),
                        InspectionStatus.SC_PENDING_APPROVAL,
                      );
                    }
                  } else {
                    await setInspectionStatus(
                      String(fkAssetInspection),
                      InspectionStatus.LI_PENDING_APPROVAL,
                    );
                  }
                } else {
                  await setInspectionStatus(
                    String(fkAssetInspection),
                    InspectionStatus.FI_SIGN_PENDING,
                  );
                }
              }
            } else if (status === TaskStatus.LI_SIGN_PENDING && result) {
              await updateStatusToLiSign(allTasksUnderSameInspection, fkAssetInspection);
            } else if (status === TaskStatus.COMPLETED && result) {
              await setInspectionStatus(String(fkAssetInspection), InspectionStatus.COMPLETED);
            }
          }
        }
        return result;
      }
    } catch (error) {
      sentryCaptureException(error, '~ file: useTaskList.tsx ~ line 57 ~ setTaskListInfo ~');
    }
  };

  const setStampingStatus = async (taskId: number, inspectionId: number, statusId: number) => {
    try {
      let result: TaskList | null;
      if (taskListCollection) {
        const isSameStatus: TaskList | null = await taskListCollection
          .findOne()
          .where('pkAssetInspectionTask')
          .eq(CurrentUserSelected.taskId)
          .exec();
        const roleId =
          isSameStatus?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL
            ? InspectorRole.LEAD
            : CurrentUserSelected?.currentUser?.clerkId
            ? InspectorRole.DEC
            : InspectorRole.FIELD;
        result = await taskListCollection
          .findOne({
            selector: {
              pkAssetInspectionTask: taskId,
            },
          })
          .update({
            $set: {
              fkInspectionTaskStatusCategory: statusId,
              role: roleId,
            },
          });
        if (statusId === TaskStatus.FI_SIGN_PENDING && result) {
          const taskResult: TaskList[] = await taskListCollection
            .find({
              selector: {
                $and: [
                  { fkAssetInspection: inspectionId },
                  { fkInspectionTaskStatusCategory: { $lt: TaskStatus.LI_SIGN_PENDING } },
                  { isActive: { $eq: true } },
                ],
              },
            })
            .exec();
          if (
            taskResult.filter(
              (i: TaskList) =>
                i.fkInspectionTaskStatusCategory === TaskStatus.IN_PROGRESS ||
                i.fkInspectionTaskStatusCategory === TaskStatus.ASSIGNED,
            )?.length === 0
          ) {
            if (
              taskResult.filter(
                (i: TaskList) =>
                  i.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL,
              )?.length === 0
            ) {
              if (
                taskResult.filter(
                  (i: TaskList) =>
                    i.fkInspectionTaskStatusCategory === TaskStatus.SC_PENDING_APPROVAL,
                )?.length === 0
              ) {
                await setInspectionStatus(
                  result.fkAssetInspection.toString(),
                  InspectionStatus.FI_SIGN_PENDING,
                );
              } else {
                await setInspectionStatus(
                  result.fkAssetInspection.toString(),
                  InspectionStatus.SC_PENDING_APPROVAL,
                );
              }
            } else {
              await setInspectionStatus(
                result.fkAssetInspection.toString(),
                InspectionStatus.LI_PENDING_APPROVAL,
              );
            }
          }
        } else if (statusId === TaskStatus.LI_SIGN_PENDING && result) {
          const taskResult: TaskList[] = await taskListCollection
            .find({
              selector: {
                $and: [{ fkAssetInspection: inspectionId }, { isActive: { $eq: true } }],
              },
            })
            .exec();
          if (
            taskResult.filter(
              (i: TaskList) => i.fkInspectionTaskStatusCategory !== TaskStatus.LI_SIGN_PENDING,
            )?.length === 0
          ) {
            await setInspectionStatus(
              result.fkAssetInspection.toString(),
              InspectionStatus.LI_SIGN_PENDING,
            );
          }
        }
        return result;
      }
    } catch (error) {
      console.log('🚀 ~ file: useTaskList. ~ line 180 ~ setStampingStatus ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: useTaskList.tsx ~ line 180 ~ setStampingStatus ~');
    }
  };

  const setTaskRecommendation = async (completeInspectionPayload: {
    recommendations: string;
    dateCompleted: string;
    proxyInspector: string;
  }) => {
    try {
      if (taskListCollection) {
        const taskData: TaskList | null = await taskListCollection
          .findOne()
          .where('pkAssetInspectionTask')
          .eq(CurrentUserSelected.taskId)
          .exec();
        const roleId =
          taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL
            ? InspectorRole.LEAD
            : CurrentUserSelected?.currentUser?.clerkId
            ? InspectorRole.DEC
            : InspectorRole.FIELD;
        await taskListCollection
          .findOne({
            selector: {
              pkAssetInspectionTask: CurrentUserSelected.taskId,
            },
          })
          .update({
            $set: {
              recommendations: completeInspectionPayload?.recommendations,
              dateCompleted: completeInspectionPayload?.dateCompleted,
              proxyInspector: completeInspectionPayload?.proxyInspector,
              fkInspectionTaskStatusCategory:
                (taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING &&
                  taskData?.fkInspectorField === taskData?.fkInspectorLead) ||
                taskData?.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING ||
                taskData?.fkInspectorLead === CurrentUserSelected?.currentUser?.inspectorId
                  ? taskData?.fkInspectionTaskStatusCategory
                  : TaskStatus.IN_PROGRESS,
              role: roleId,
            },
          });
      }
    } catch (error) {
      console.log('~ file: useTaskList.tsx ~ line 245 ~ setTaskRecommendation ~', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: useTaskList.tsx ~ line 245 ~ setTaskRecommendation ~');
    }
  };

  const setTaskLevelFIComment = async (taskId: number, fieldComment: string) => {
    try {
      if (taskListCollection) {
        const isSameStatus: TaskList | null = await taskListCollection
          .findOne()
          .where('pkAssetInspectionTask')
          .eq(CurrentUserSelected.taskId)
          .exec();
        const roleId =
          isSameStatus?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL
            ? InspectorRole.LEAD
            : CurrentUserSelected?.currentUser?.clerkId
            ? InspectorRole.DEC
            : InspectorRole.FIELD;
        const result: TaskList | null = await taskListCollection
          .findOne({
            selector: {
              pkAssetInspectionTask: taskId,
            },
          })
          .update({
            $set: {
              commentInspectorField: fieldComment,
              role: roleId,
            },
          });
        return result;
      }
    } catch (error) {
      console.log('🚀 ~ file: useTaskList. ~ line 265 ~ setTaskLevelFIComment ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: useTaskList.tsx ~ line 265 ~ setTaskLevelFIComment ~');
    }
  };

  const setTaskLevelComment = async (
    taskId: number,
    leadComment: string,
    fieldComment: string,
    statusId: number,
    isAssignedBack: boolean,
    attentionFlag: boolean,
  ) => {
    try {
      if (taskListCollection) {
        const isSameStatus: TaskList | null = await taskListCollection
          .findOne()
          .where('pkAssetInspectionTask')
          .eq(CurrentUserSelected.taskId)
          .exec();
        const roleId =
          isSameStatus?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL ||
          isSameStatus?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING
            ? InspectorRole.LEAD
            : CurrentUserSelected?.currentUser?.clerkId
            ? InspectorRole.DEC
            : InspectorRole.FIELD;
        const result: TaskList | null = await taskListCollection
          .findOne({
            selector: {
              pkAssetInspectionTask: taskId,
            },
          })
          .update({
            $set: {
              commentInspectorLead: leadComment,
              commentInspectorField: fieldComment,
              fkInspectionTaskStatusCategory: statusId,
              isAssignedBack,
              attentionFlag,
              role: roleId,
            },
          });
        if (statusId === TaskStatus.SC_PENDING_APPROVAL && result) {
          const taskResult = await taskListCollection
            .find({
              selector: {
                $and: [
                  { fkAssetInspection: CurrentUserSelected.inspectionId },
                  { isActive: { $eq: true } },
                ],
              },
            })
            .exec();
          if (
            taskResult.filter(
              (i: TaskList) => i.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL,
            )?.length === 0
          ) {
            if (
              taskResult.filter(
                (i: TaskList) =>
                  i.fkInspectionTaskStatusCategory === TaskStatus.IN_PROGRESS ||
                  i.fkInspectionTaskStatusCategory === TaskStatus.ASSIGNED,
              )?.length === 0
            ) {
              await setInspectionStatus(
                result.fkAssetInspection.toString(),
                InspectionStatus.SC_PENDING_APPROVAL,
              );
            } else {
              await setInspectionStatus(
                result.fkAssetInspection.toString(),
                InspectionStatus.IN_PROGRESS,
              );
            }
          }
        } else if (statusId === TaskStatus.IN_PROGRESS && result) {
          if (result.isAssignedBack) {
            const taskResult = await taskListCollection
              .find({
                selector: {
                  $and: [
                    { fkAssetInspection: CurrentUserSelected.inspectionId },
                    { fkInspectionTaskStatusCategory: TaskStatus.LI_PENDING_APPROVAL },
                    { isActive: { $eq: true } },
                  ],
                },
              })
              .exec();
            if (taskResult?.length === 0) {
              await setInspectionStatus(
                result.fkAssetInspection.toString(),
                InspectionStatus.IN_PROGRESS,
              );
            }
          }
        } else if (statusId === TaskStatus.FI_SIGN_PENDING && result) {
          await setInspectionStatus(
            result.fkAssetInspection.toString(),
            InspectionStatus.FI_SIGN_PENDING,
          );
        } else if (statusId === TaskStatus.LI_SIGN_PENDING && result) {
          const fkAssetInspection = result.fkAssetInspection;
          const allTasksUnderSameInspection = await taskListCollection
            .find({ selector: { $and: [{ fkAssetInspection }, { isActive: { $eq: true } }] } })
            .exec();

          const siblingTasks = allTasksUnderSameInspection.filter(
            task =>
              ![
                TaskStatus.LI_SIGN_PENDING,
                TaskStatus.SC_PENDING_APPROVAL,
                TaskStatus.COMPLETED,
              ].includes(task.fkInspectionTaskStatusCategory),
          );

          if (siblingTasks.length === 0) {
            await setInspectionStatus(String(fkAssetInspection), InspectionStatus.LI_SIGN_PENDING);
          }
        }
        return result;
      }
    } catch (error) {
      console.log('🚀 ~ file: useTaskList. ~ line 115 ~ setTaskLevelComment ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: useTaskList.tsx ~ line 57 ~ setTaskListInfo ~');
    }
  };

  const getTaskList = async (): Promise<TaskListDocument[]> => {
    if (!taskListCollection) return [];
    let taskList: TaskListDocument[];
    try {
      if (CurrentUserSelected.isAdhoc) {
        taskList = await taskListCollection
          ?.find({
            selector: {
              $and: [{ isActive: { $eq: true } }, { site }, { discipline }],
            },
          })
          .exec();

        const inspectionList: InspectionListDocument[] = await inspectionListCollection
          ?.find({
            selector: {
              $and: [
                { isAdhoc: CurrentUserSelected.isAdhoc },
                { assetInspectionStatusId: { $ne: InspectionStatus.COMPLETED } },
                { site },
                { discipline },
              ],
            },
          })
          .exec();

        return taskList.filter(task =>
          inspectionList.filter(
            inspection => task.fkAssetInspection === Number(inspection.inspectionId),
          ),
        );
      } else {
        taskList = await taskListCollection
          ?.find({
            selector: {
              $and: [
                { fkSchedule: CurrentUserSelected.workPackageId },
                { isActive: { $eq: true } },
              ],
            },
          })
          .exec();
        return taskList;
      }
    } catch (error) {
      console.log('🚀 ~ file: useTaskList.ts ~ line 133 ~ getTaskList ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: useTaskList.tsx ~ line 57 ~ getTaskList ~');
      throw new Error(`error ${error}`);
    }
  };

  const checkTaskIsThere = async (
    workPackageId: number,
    inspectorId: number,
    clerkId: number,
  ): Promise<TaskListDocument[]> => {
    if (!taskListCollection) return [];
    let taskList: TaskListDocument[];
    try {
      taskList = await taskListCollection
        ?.find({
          selector: {
            $and: [
              { fkSchedule: workPackageId },
              {
                $or: [{ fkInspectorField: inspectorId }, { clerkId }],
              },
            ],
          },
        })
        .exec();
      let isThere = [];
      isThere = taskList.filter(
        task =>
          task.fkInspectionTaskStatusCategory === TaskStatus.COMPLETED ||
          task.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING ||
          task.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING,
      );
      return isThere;
    } catch (error) {
      console.log('🚀 ~ file: useTaskList.ts ~ line 398 ~ checkTaskIsThere ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: useTaskList.tsx ~ line 398 ~ checkTaskIsThere ~');
      throw new Error(`error ${error}`);
    }
  };

  const setTaskComment = async (taskId: number, comment: string, attentionFlag: boolean) => {
    try {
      if (taskListCollection) {
        const isSameStatus: TaskList | null = await taskListCollection
          .findOne()
          .where('pkAssetInspectionTask')
          .eq(CurrentUserSelected.taskId)
          .exec();
        const roleId =
          isSameStatus?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL ||
          isSameStatus?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING
            ? InspectorRole.LEAD
            : CurrentUserSelected?.currentUser?.clerkId
            ? InspectorRole.DEC
            : InspectorRole.FIELD;
        const result: TaskList | null = await taskListCollection
          .findOne({
            selector: { pkAssetInspectionTask: taskId },
          })
          .update({
            $set: {
              commentInspectorField: comment,
              attentionFlag,
              role: roleId,
            },
          });
        return result;
      }
    } catch (error) {
      console.log('🚀 ~ file: useTaskList. ~ line 422 ~ setTaskComment ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: useTaskList.tsx ~ line 422 ~ setTaskComment ~');
    }
  };

  const updateAllTaskStatus = async (
    fkAssetInspection: number,
    fkInspectionTaskStatusCategory: number,
    role: number,
  ) => {
    try {
      const task = await taskListCollection?.find({ selector: { fkAssetInspection } }).update({
        $set: { fkInspectionTaskStatusCategory, role },
      });
      const inspectionStatusId =
        fkInspectionTaskStatusCategory === TaskStatus.SC_PENDING_APPROVAL
          ? InspectionStatus.SC_PENDING_APPROVAL
          : fkInspectionTaskStatusCategory;
      await setInspectionStatus(String(fkAssetInspection), inspectionStatusId);
      return task;
    } catch (error) {
      sentryCaptureException(error, '~ file: useTaskList.tsx ~ line 661 ~ updateAllTaskStatus ~');
      throw error;
    }
  };

  const updateTaskStatusNewFlow = async (
    pkAssetInspectionTask: number,
    fkInspectionTaskStatusCategory: number,
  ) => {
    try {
      if (taskListCollection) {
        const existingTask: TaskList | null = await taskListCollection
          .findOne({ selector: { pkAssetInspectionTask } })
          .exec();

        if (
          existingTask &&
          existingTask?.fkInspectionTaskStatusCategory !== fkInspectionTaskStatusCategory
        ) {
          const fkAssetInspection = existingTask?.fkAssetInspection;
          const roleId =
            existingTask?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL
              ? InspectorRole.LEAD
              : CurrentUserSelected?.currentUser?.clerkId
              ? InspectorRole.DEC
              : InspectorRole.FIELD;

          const result = await taskListCollection
            .findOne({ selector: { pkAssetInspectionTask } })
            .update({ $set: { fkInspectionTaskStatusCategory, role: roleId } });

          if (result && fkInspectionTaskStatusCategory === TaskStatus.IN_PROGRESS) {
            await setInspectionStatus(String(fkAssetInspection), InspectionStatus.IN_PROGRESS);
          } else {
            const allTasksUnderSameInspection = await taskListCollection
              .find({ selector: { $and: [{ fkAssetInspection }, { isActive: { $eq: true } }] } })
              .exec();

            if (result && fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL) {
              await updateStatusFromLiReview(allTasksUnderSameInspection, fkAssetInspection);
            }
          }
        }
        return existingTask;
      }
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useTaskList.tsx ~ line 730 ~ updateTaskStatusNewFlow ~',
      );
    }
  };

  const updateTaskStatusesAfterLiSign = async (fkAssetInspection: number) => {
    try {
      if (taskListCollection) {
        const taskList = await taskListCollection
          .find({
            selector: {
              $and: [
                { fkAssetInspection },
                { fkInspectionTaskStatusCategory: { $eq: TaskStatus.LI_SIGN_PENDING } },
                { isActive: true },
              ],
            },
          })
          .exec();

        if (taskList.length) {
          for (let i = 0; i < taskList?.length; i++) {
            await taskListCollection
              .find({ selector: { pkAssetInspectionTask: taskList[i].pkAssetInspectionTask } })
              .update({
                $set: {
                  fkInspectionTaskStatusCategory:
                    taskList[i].fkInspectorField === taskList[i].fkInspectorLead &&
                    taskList[i].fieldInspectorUserId === taskList[i].siteCoordinatorUserId
                      ? TaskStatus.COMPLETED
                      : TaskStatus.SC_PENDING_APPROVAL,
                  role: InspectorRole.LEAD,
                },
              });
          }
          const allTaskUnderInspection = await taskListCollection
            .find({
              selector: {
                $and: [
                  { fkAssetInspection },
                  { fkInspectionTaskStatusCategory: { $ne: TaskStatus.COMPLETED } },
                  { isActive: true },
                ],
              },
            })
            .exec();

          await setInspectionStatus(
            String(fkAssetInspection),
            allTaskUnderInspection?.length > 0
              ? InspectionStatus.SC_PENDING_APPROVAL
              : InspectionStatus.COMPLETED,
          );
        }
        return taskList;
      }
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useTaskList.tsx ~ line 803 ~ updateTaskStatusesAfterLiSign ~',
      );
    }
  };

  return {
    taskListInitialized,
    setTaskLevelComment,
    taskListCollection,
    setTaskListInfo,
    setTaskLevelFIComment,
    getTaskList,
    checkTaskIsThere,
    setTaskComment,
    setTaskRecommendation,
    setStampingStatus,
    updateAllTaskStatus,
    updateTaskStatusNewFlow,
    updateTaskStatusesAfterLiSign,
  };
};
